import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {LoadComponent} from '@brunata-angular/ui';
import {filterNulls, TRANSLATE} from '@brunata-angular/util';
import {CreateEventAlarmComponent} from '../create-event-alarm/create-event-alarm.component';
import {combineLatest, filter, map, Observable, shareReplay, switchMap} from 'rxjs';
import {Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {EventAlarmsService} from '../event-alarms.service';
import {selectBuilding} from '../../../state/selectors';
import {ToastrService} from 'ngx-toastr';
import {toSignal} from '@angular/core/rxjs-interop';
import {EventAlarmUpdateRequest} from '../event-alarm.model';
import {HasUnsavedChanges} from '@brunata-angular/ui';

@Component({
    selector: 'app-edit-event-alarm',
    standalone: true,
    imports: [LoadComponent, ReactiveFormsModule, CreateEventAlarmComponent],
    template: `
        @if (viewSignal(); as view) {
            <app-create-event-alarm
                [eventTypeId]="view.eventTypeId"
                [initialFormValues]="view.eventAlarm"
                (updateEventAlarm)="updateEventAlarm(view.buildingNo, view.subscriptionId, $event)"
                (unsavedChanges)="this.hasUnsavedChangesSignal.set($event)" />
        } @else {
            <app-load />
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditEventAlarmComponent implements HasUnsavedChanges {
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly translate = inject(TRANSLATE);
    private readonly toastrService = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly eventAlarmService = inject(EventAlarmsService);

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter(buildingState => buildingState.status === 'success'),
        map(({buildingNo}) => buildingNo),
    );

    private readonly subscriptionId$: Observable<string> = this.activatedRoute.paramMap.pipe(
        map(params => params.get('id')),
        filterNulls(),
    );

    private readonly eventAlarm$ = combineLatest([this.buildingNo$, this.subscriptionId$]).pipe(
        switchMap(([buildingNo, subscriptionId]) =>
            this.eventAlarmService.getEventAlarm(buildingNo, subscriptionId).pipe(
                map(eventAlarm => ({
                    ...eventAlarm,
                    eventTypeId: eventAlarm.eventTypeId,
                    submitted: false,
                    vendorModels: eventAlarm.vendorModels.map(vendorModel => ({
                        ...vendorModel,
                        isEventTypeSupported: true,
                        selected: true,
                    })),
                })),
            ),
        ),
        shareReplay(1),
    );

    private readonly eventAlarmSignal = toSignal(this.eventAlarm$);
    private readonly buildingNoSignal = toSignal(this.buildingNo$);
    private readonly subscriptionIdSignal = toSignal(this.subscriptionId$);

    readonly hasUnsavedChangesSignal = signal(false);

    readonly viewSignal = computed(() => {
        const eventAlarm = this.eventAlarmSignal();
        const buildingNo = this.buildingNoSignal();
        const subscriptionId = this.subscriptionIdSignal();

        if (buildingNo === undefined || subscriptionId === undefined || eventAlarm === undefined) {
            return;
        }

        return {eventTypeId: eventAlarm.eventTypeId, eventAlarm, buildingNo, subscriptionId};
    });

    updateEventAlarm(
        buildingNo: string,
        subscriptionId: string,
        request: EventAlarmUpdateRequest,
    ): void {
        this.eventAlarmService
            .updateEventAlarm(buildingNo, subscriptionId, request)
            .subscribe(() => {
                const successMessage = this.translate('event-alarms_alarm_updated').replace(
                    '{0}',
                    request.alarmName,
                );

                this.toastrService.success(successMessage);
                void this.router.navigate([
                    'admin',
                    'system',
                    buildingNo,
                    'monitoring',
                    'event-alarms',
                ]);
            });
    }

    hasUnsavedChanges(): boolean {
        return this.hasUnsavedChangesSignal();
    }
}
